import React, { FC, useMemo, useEffect } from 'react';
import DOMPurify from 'dompurify';

import coreLogger from '../../../utils/log/coreLogger';
import { StyledContainer } from './SafeHtml.styles';

export interface SafeHtmlProps {
  /** String containing some HTML. */
  children: string;
  /** Override the margin/padding of the first and last child */
  noSpacing?: boolean;
  /** Specifies custom HTML tags to accept.  E.g. `addTags: ['style']` */
  addTags?: string[];
  /** Specifies additional HTML tags to forbid.  E.g. `forbidTags: ['style']` */
  forbidTags?: string[];
  /** Specifies additional HTML tags to accept. E.g. `addAttr: ['src']` */
  allowedTags?: string[];
  /** Specifies additional HTML tags to accept. E.g. `addAttr: ['src']` */
  allowedAttr?: string[];
  /** Specifies additional HTML attributes to forbid. E.g. `forbidAttr: ['class']` */
  forbidAttr?: string[];
  /** Specifies custom attribute to accept. E.g. `addAttr: ['src']` */
  addAttr?: string[];
}

const SafeHtmlDefaultProps: Partial<SafeHtmlProps> = {
  noSpacing: true,
  allowedTags: ['a', 'abbr', 'acronym', 'address', 'article', 'aside', 'b', 'big', 'blockquote', 'br', 'center', 'code', 'col', 'colgroup', 'content', 'dd', 'div', 'dl', 'dt', 'em', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'i', 'label', 'li', 'nav', 'ol', 'p', 'pre', 'q', 's', 'section', 'small', 'span', 'strike', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul'],
  allowedAttr: ['align', 'alt', 'class', 'cols', 'colspan', 'for', 'height', 'href', 'id', 'label', 'lang', 'name', 'role', 'rowspan', 'span', 'style', 'title', 'width', 'data-testid', 'data-analytics-id', 'target'],
  forbidTags: [],
  forbidAttr: [],
  addTags: [],
  addAttr: [],
};

const SafeHtml: FC<SafeHtmlProps> = ({ children, allowedTags, allowedAttr, forbidTags, forbidAttr, addTags, addAttr, ...props }) => {
  const isStringChildren = useMemo(() => typeof children === 'string', [children]);

  const domPurityOptions = useMemo(() => ({
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: forbidAttr,
    ADD_TAGS: addTags,
    ADD_ATTR: addAttr,
  }), [allowedTags, allowedAttr, forbidTags, forbidAttr, addTags, addAttr]);

  const sanitizedHtml = useMemo(() => ({
    __html: isStringChildren
      ? DOMPurify.sanitize(children, domPurityOptions)
      : '',
  }), [children, domPurityOptions, isStringChildren]);

  useEffect(() => {
    if (!isStringChildren) {
      coreLogger.warn('SafeHtml: The children must be a string.');
    }
  }, [isStringChildren]);

  return (
    // eslint-disable-next-line react/no-danger
    <StyledContainer dangerouslySetInnerHTML={sanitizedHtml} {...props} />
  );
};

SafeHtml.defaultProps = SafeHtmlDefaultProps;

export default SafeHtml;
